import { useState, useContext, useMemo } from "react";
import BryntumContext from "@/react/context/bryntumContext";
// @ts-expect-error - userPreferences is dynamically set in bryntumContext
import { userPreferences } from "@/react/context/bryntumContext.jsx";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { CARD_TYPE_MAP } from "../constants/settingsStructure";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { ContextCard } from "@/bryntum-lib/models/ContextCard";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { toast } from "sonner";
import { isIAmOneOfBoardOwner } from "@/other/utils/utils";

export const useContextCards = (subcategoryId: string) => {
  const { user, project, boardManager, selectedBoard } = useContext(BryntumContext);
  const [saving, setSaving] = useState(false);

  const cardType = CARD_TYPE_MAP[subcategoryId];
  const canRead = user?.can(GRANTS.grant_three); // scheduler+

  // Permission check: board owner only, excluding demo users
  // @ts-expect-error - boardManager is dynamically set in bryntumContext
  const boardStore = boardManager?.boardNameStore?.getById(selectedBoard?.id);
  const isBoardOwner = project && boardStore ? isIAmOneOfBoardOwner(project, boardStore.owner) : false;
  const canWrite = isBoardOwner && !user?.isDemoUser;

  // @ts-expect-error - contextCardsStore is added dynamically in projectConfig
  const contextCardsStore = project?.contextCardsStore;

  // Subscribe to store changes - triggers rerender automatically
  const { version, forceRerender } = useSwivuStore<ContextCard>({
    storeId: STORE_KEYS.contextCardsStore
  });

  // Get cards from store
  const cards = useMemo(() => {
    if (!contextCardsStore || !cardType) return [];

    return contextCardsStore
      .query(
        (r: ContextCard) => r.cardType === cardType,
        true // Include filtered records
      )
      .map((r: ContextCard) => ({
        id: String(r.id),
        content: r.content,
        enabled: r.isEnabled,
        cardType: r.cardType,
        isDraft: r.isDraft
      }));
  }, [contextCardsStore, cardType, version]);

  // Save: add new or update existing via store
  const handleSave = async (id: string, data: { content: string; enabled: boolean }, isRequired: boolean = false) => {
    if (!canWrite) {
      toast.error("You don't have permission to modify context cards");
      return;
    }

    if (!contextCardsStore || !project) return;

    // Prevent disabling the last enabled card in required sections
    if (isRequired && !data.enabled) {
      const enabledCards = cards.filter((c: { enabled: boolean; id: string }) => c.enabled && c.id !== id);
      if (enabledCards.length === 0) {
        toast.warning("At least one card must be enabled in required sections");
        return;
      }
    }

    setSaving(true);

    let recordToAddInStore: ContextCard | undefined;

    try {
      const isNewCard = id.startsWith("new-");
      const record = contextCardsStore?.getById(id);

      if (!record || isNewCard) {
        // Add new record
        recordToAddInStore = contextCardsStore?.add({
          content: data.content,
          isEnabled: data.enabled,
          cardType: cardType
        })?.[0];
      } else {
        // Update existing record
        record.set({
          content: data.content,
          isEnabled: data.enabled
        });
      }

      // Commit changes to backend
      // @ts-expect-error - project type not fully defined
      await project.sync()

      // Set board preference to indicate draft changes
      // @ts-expect-error - userPreferences is dynamically set in bryntumContext
      userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.aiContextCardInDraft, "true", true);
    } catch (error) {
      if (recordToAddInStore) {
        recordToAddInStore.remove();
        contextCardsStore.remove(recordToAddInStore);
        forceRerender()
      }

      console.error("Error saving context card:", error);
    } finally {
      setSaving(false);
    }
  };

  // Delete via store
  const handleDelete = async (id: string, cardsLength: number, isRequired: boolean) => {
    if (!canWrite) {
      toast.error("You don't have permission to delete context cards");
      return;
    }

    // Prevent deleting last card if required type
    if (isRequired && cardsLength <= 1) {
      toast.warning("At least one card is required for this section");
      return;
    }

    if (!contextCardsStore || !project) return;

    setSaving(true);

    try {
      const record = contextCardsStore?.getById(id);
      if (record) {
        contextCardsStore?.remove(record);
        // Commit changes to backend
        // @ts-expect-error - project type not fully defined
        await project.sync();

        // Set board preference to indicate draft changes
        // @ts-expect-error - userPreferences is dynamically set in bryntumContext
        userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.aiContextCardInDraft, "true", true);
      }
    } catch (error) {
      console.error("Error deleting context card:", error);
      toast.error("Failed to delete card. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return {
    cards,
    saving,
    canRead,
    canWrite,
    handleSave,
    handleDelete
  };
};
